import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  Check,
  Clock,
  Globe,
  Loader2,
  MapPin,
  Phone,
  Plus,
  RefreshCw,
  Trash2,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabaseClient';
import { invokeErrorMessage } from '../lib/supabaseFunctionError';

type TimeOfDay = { hours?: number; minutes?: number };
type Period = { openDay: string; openTime: TimeOfDay; closeDay: string; closeTime: TimeOfDay };
type SpecialPeriod = {
  startDate: { year: number; month: number; day: number };
  endDate?: { year: number; month: number; day: number };
  closed?: boolean;
  openTime?: TimeOfDay;
  closeTime?: TimeOfDay;
};

interface GbpLocation {
  name: string;
  title?: string;
  phoneNumbers?: { primaryPhone?: string; additionalPhones?: string[] };
  websiteUri?: string;
  regularHours?: { periods?: Period[] };
  specialHours?: { specialHourPeriods?: SpecialPeriod[] };
  openInfo?: { status?: string; canReopen?: boolean };
  categories?: { primaryCategory?: { displayName?: string }; additionalCategories?: Array<{ displayName?: string }> };
  profile?: { description?: string };
  storefrontAddress?: { addressLines?: string[]; locality?: string; administrativeArea?: string; postalCode?: string };
  metadata?: { mapsUri?: string; newReviewUri?: string; placeId?: string };
}

interface GbpAdmin {
  name: string;
  email: string | null;
  role: string;
  pendingInvitation: boolean;
}

interface ProfileData {
  location: GbpLocation;
  googleUpdated: { location: GbpLocation | null; diffMask: string[]; pendingMask: string[] } | null;
  googleUpdatedError: string | null;
  admins: GbpAdmin[];
  adminsError: string | null;
}

const DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'] as const;
const DAY_LABEL: Record<string, string> = {
  MONDAY: 'Mon', TUESDAY: 'Tue', WEDNESDAY: 'Wed', THURSDAY: 'Thu', FRIDAY: 'Fri', SATURDAY: 'Sat', SUNDAY: 'Sun',
};
/** Mirrors the edge function's PATCH whitelist. */
const EDITABLE_TOP_LEVEL = new Set([
  'title', 'phoneNumbers', 'websiteUri', 'regularHours', 'specialHours', 'moreHours', 'openInfo', 'profile', 'labels',
]);
const ROLE_LABEL: Record<string, string> = {
  PRIMARY_OWNER: 'Primary owner',
  OWNER: 'Owner',
  MANAGER: 'Manager',
  SITE_MANAGER: 'Site manager',
};

/** "HH:MM" for <input type="time"> */
function toInputTime(t?: TimeOfDay): string {
  if (!t) return '';
  const h = Number(t.hours || 0);
  const m = Number(t.minutes || 0);
  if (h === 24) return '23:59';
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
function fromInputTime(v: string): TimeOfDay {
  const [h, m] = v.split(':').map((n) => Number(n) || 0);
  return { hours: h, minutes: m };
}
function fmtTime(t?: TimeOfDay): string {
  if (!t) return '';
  const h = Number(t.hours || 0);
  if (h === 24) return 'midnight';
  const m = Number(t.minutes || 0);
  const d = new Date(2000, 0, 1, h, m);
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: m ? '2-digit' : undefined }).toLowerCase();
}
function fmtGoogleDate(d?: { year: number; month: number; day: number }): string {
  if (!d) return '';
  return new Date(d.year, d.month - 1, d.day).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

type HoursDraft = Record<string, { closed: boolean; periods: Array<{ open: string; close: string }> }>;

function hoursToDraft(periods: Period[] | undefined): HoursDraft {
  const draft: HoursDraft = {};
  for (const day of DAYS) draft[day] = { closed: true, periods: [] };
  for (const p of periods || []) {
    const day = String(p.openDay || '').toUpperCase();
    if (!draft[day]) continue;
    draft[day].closed = false;
    draft[day].periods.push({ open: toInputTime(p.openTime), close: toInputTime(p.closeTime) });
  }
  for (const day of DAYS) {
    if (!draft[day].closed && draft[day].periods.length === 0) draft[day].closed = true;
  }
  return draft;
}

function draftToPeriods(draft: HoursDraft): Period[] {
  const out: Period[] = [];
  for (const day of DAYS) {
    const d = draft[day];
    if (!d || d.closed) continue;
    for (const p of d.periods) {
      if (!p.open || !p.close) continue;
      const openTime = fromInputTime(p.open);
      const closeTime = p.close === '23:59' ? { hours: 24, minutes: 0 } : fromInputTime(p.close);
      // Google represents "open 24 hours" as 00:00 → 24:00 on the same day.
      out.push({ openDay: day, openTime, closeDay: day, closeTime });
    }
  }
  return out;
}

function describeField(field: string): string {
  const map: Record<string, string> = {
    title: 'Business name',
    'phoneNumbers.primaryPhone': 'Phone number',
    phoneNumbers: 'Phone numbers',
    websiteUri: 'Website',
    regularHours: 'Opening hours',
    specialHours: 'Special hours',
    'profile.description': 'Description',
    profile: 'Description',
    categories: 'Categories',
    storefrontAddress: 'Address',
    openInfo: 'Open status',
    'openInfo.status': 'Open status',
    serviceItems: 'Services & prices',
    serviceArea: 'Service area',
    labels: 'Labels',
    moreHours: 'More hours',
  };
  return map[field] || field.replace(/([A-Z])/g, ' $1').replace(/\./g, ' › ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

interface GoogleBusinessProfilePanelProps {
  businessId: string;
  connectionId: string;
  open: boolean;
  onClose: () => void;
  onLocationRenamed?: (title: string) => void;
}

export default function GoogleBusinessProfilePanel({
  businessId,
  connectionId,
  open,
  onClose,
  onLocationRenamed,
}: GoogleBusinessProfilePanelProps) {
  const [data, setData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('OPEN');
  const [hours, setHours] = useState<HoursDraft>(() => hoursToDraft(undefined));
  const [special, setSpecial] = useState<SpecialPeriod[]>([]);
  const [newSpecialDate, setNewSpecialDate] = useState('');

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'MANAGER' | 'OWNER'>('MANAGER');
  const [showInvite, setShowInvite] = useState(false);

  const invoke = useCallback(
    async (body: Record<string, unknown>) => {
      const { data: res, error: err } = await supabase.functions.invoke('google-gbp-profile', {
        body: { businessId, connectionId, ...body },
      });
      if (err || res?.error) throw new Error((await invokeErrorMessage(err, res)) || 'Request failed');
      return res;
    },
    [businessId, connectionId],
  );

  const applyLocation = useCallback((loc: GbpLocation) => {
    setTitle(loc.title || '');
    setPhone(loc.phoneNumbers?.primaryPhone || '');
    setWebsite(loc.websiteUri || '');
    setDescription(loc.profile?.description || '');
    setStatus(loc.openInfo?.status || 'OPEN');
    setHours(hoursToDraft(loc.regularHours?.periods));
    setSpecial(loc.specialHours?.specialHourPeriods || []);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await invoke({ action: 'get' });
      if (res?.unsupported) {
        setError('Profile editing needs the direct Google connection. Reconnect Google Business Profile.');
        return;
      }
      const next: ProfileData = {
        location: res.location,
        googleUpdated: res.googleUpdated || null,
        googleUpdatedError: res.googleUpdatedError || null,
        admins: Array.isArray(res.admins) ? res.admins : [],
        adminsError: res.adminsError || null,
      };
      setData(next);
      applyLocation(next.location);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [invoke, applyLocation]);

  useEffect(() => {
    if (open && !data && !loading) void load();
  }, [open, data, loading, load]);

  useEffect(() => {
    setData(null);
  }, [connectionId]);

  const loc = data?.location;

  const detailsDirty = useMemo(() => {
    if (!loc) return false;
    return (
      title.trim() !== (loc.title || '') ||
      phone.trim() !== (loc.phoneNumbers?.primaryPhone || '') ||
      website.trim() !== (loc.websiteUri || '') ||
      description !== (loc.profile?.description || '') ||
      status !== (loc.openInfo?.status || 'OPEN')
    );
  }, [loc, title, phone, website, description, status]);

  const hoursDirty = useMemo(() => {
    if (!loc) return false;
    return JSON.stringify(draftToPeriods(hours)) !== JSON.stringify(draftToPeriods(hoursToDraft(loc.regularHours?.periods)));
  }, [loc, hours]);

  const specialDirty = useMemo(() => {
    if (!loc) return false;
    return JSON.stringify(special) !== JSON.stringify(loc.specialHours?.specialHourPeriods || []);
  }, [loc, special]);

  const save = async (label: string, patch: Record<string, unknown>) => {
    setSaving(label);
    try {
      const res = await invoke({ action: 'updateLocation', patch });
      toast.success(`${label} updated on Google`);
      if (res?.location) {
        setData((d) => (d ? { ...d, location: { ...d.location, ...res.location } } : d));
        applyLocation({ ...(loc as GbpLocation), ...res.location });
        if (patch.title && res.location.title) onLocationRenamed?.(String(res.location.title));
      } else {
        await load();
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Failed to update ${label.toLowerCase()}`);
    } finally {
      setSaving(null);
    }
  };

  const saveDetails = () => {
    if (!loc) return;
    const patch: Record<string, unknown> = {};
    if (title.trim() !== (loc.title || '')) patch.title = title.trim();
    if (phone.trim() !== (loc.phoneNumbers?.primaryPhone || '')) {
      patch.phoneNumbers = { ...(loc.phoneNumbers || {}), primaryPhone: phone.trim() };
    }
    if (website.trim() !== (loc.websiteUri || '')) patch.websiteUri = website.trim();
    if (description !== (loc.profile?.description || '')) patch.profile = { description };
    if (status !== (loc.openInfo?.status || 'OPEN')) patch.openInfo = { status };
    void save('Business details', patch);
  };

  const saveHours = () => void save('Opening hours', { regularHours: { periods: draftToPeriods(hours) } });
  const saveSpecial = () => void save('Special hours', { specialHours: { specialHourPeriods: special } });

  const acceptGoogleEdit = (field: string) => {
    const g = data?.googleUpdated?.location;
    if (!g) return;
    const top = field.split('.')[0] as keyof GbpLocation;
    const value = g[top];
    if (value === undefined) return;
    void save(describeField(top), { [top]: value });
  };
  const keepMine = (field: string) => {
    if (!loc) return;
    const top = field.split('.')[0] as keyof GbpLocation;
    const value = loc[top];
    if (value === undefined) return;
    void save(describeField(top), { [top]: value });
  };

  const adminAction = async (label: string, body: Record<string, unknown>) => {
    setSaving(label);
    try {
      await invoke({ action: 'admin', ...body });
      toast.success(label);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setSaving(null);
    }
  };

  if (!open) return null;

  const address = loc?.storefrontAddress
    ? [
        ...(loc.storefrontAddress.addressLines || []),
        [loc.storefrontAddress.locality, loc.storefrontAddress.administrativeArea, loc.storefrontAddress.postalCode]
          .filter(Boolean)
          .join(', '),
      ]
        .filter(Boolean)
        .join(', ')
    : '';

  // Only offer Accept/Keep for fields this panel can actually write back;
  // everything else Google touched is shown for awareness only.
  const isEditableField = (f: string) => EDITABLE_TOP_LEVEL.has(f.split('.')[0]);
  const isNoiseField = (f: string) => !f || f === 'name' || f.split('.')[0] === 'metadata';
  const allDiff = (data?.googleUpdated?.diffMask || []).filter((f) => !isNoiseField(f));
  const diffFields = allDiff.filter(isEditableField);
  const infoDiffFields = allDiff.filter((f) => !isEditableField(f));
  const pendingFields = (data?.googleUpdated?.pendingMask || []).filter((f) => !isNoiseField(f));

  return createPortal(
    <div className="fixed inset-0 z-[100080] flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className="relative bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[92vh] sm:max-h-[88vh] flex flex-col shadow-xl overflow-hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        role="dialog"
        aria-modal="true"
        aria-label="Google Business Profile details"
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 shrink-0">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900">Business Profile</h3>
            <p className="text-[11px] text-gray-500 truncate">What customers see on Google Search & Maps</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => void load()}
              disabled={loading}
              aria-label="Refresh"
              className="h-8 w-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button type="button" onClick={onClose} aria-label="Close" className="h-8 w-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="p-4 overflow-y-auto space-y-5">
          {error ? (
            <p className="text-sm text-red-600 text-center py-10">{error}</p>
          ) : !loc ? (
            <div className="flex justify-center py-16 text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <>
              {/* Google's edits */}
              {diffFields.length > 0 || infoDiffFields.length > 0 || pendingFields.length > 0 ? (
                <section className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2">
                  <p className="text-[12px] font-semibold text-amber-900 inline-flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {diffFields.length > 0 || infoDiffFields.length > 0
                      ? 'Google has changed your listing'
                      : 'Google is reviewing changes to your listing'}
                  </p>
                  {diffFields.length > 0 ? (
                    <ul className="space-y-1.5">
                      {diffFields.map((f) => (
                        <li key={f} className="flex items-center justify-between gap-2 text-[12px] text-amber-900">
                          <span className="truncate">{describeField(f)}</span>
                          <span className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              disabled={!!saving}
                              onClick={() => acceptGoogleEdit(f)}
                              className="px-2 py-1 rounded-md bg-white border border-amber-300 text-[11px] font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                            >
                              Accept Google's
                            </button>
                            <button
                              type="button"
                              disabled={!!saving}
                              onClick={() => keepMine(f)}
                              className="px-2 py-1 rounded-md bg-amber-600 text-[11px] font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                            >
                              Keep mine
                            </button>
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {infoDiffFields.length > 0 ? (
                    <p className="text-[11px] text-amber-800">
                      Also updated by Google (review in the Business Profile app):{' '}
                      {infoDiffFields.map(describeField).join(', ')}
                    </p>
                  ) : null}
                  {pendingFields.length > 0 ? (
                    <p className="text-[11px] text-amber-800">
                      Awaiting Google review: {pendingFields.map(describeField).join(', ')}
                    </p>
                  ) : null}
                </section>
              ) : null}

              {/* Details */}
              <section className="space-y-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Business details</p>
                <div className="grid gap-3">
                  <label className="block">
                    <span className="text-[11px] font-medium text-gray-500">Business name</span>
                    <input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-[11px] font-medium text-gray-500 inline-flex items-center gap-1"><Phone className="h-3 w-3" /> Phone</span>
                      <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
                    </label>
                    <label className="block">
                      <span className="text-[11px] font-medium text-gray-500 inline-flex items-center gap-1"><Globe className="h-3 w-3" /> Website</span>
                      <input value={website} onChange={(e) => setWebsite(e.target.value)} inputMode="url" placeholder="https://" className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
                    </label>
                  </div>
                  <label className="block">
                    <span className="text-[11px] font-medium text-gray-500 flex items-center justify-between">
                      <span>Description</span>
                      <span className={`tabular-nums ${description.length > 750 ? 'text-red-500' : 'text-gray-400'}`}>{description.length}/750</span>
                    </span>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      maxLength={750}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 resize-y"
                    />
                  </label>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                      <span className="text-[11px] font-medium text-gray-500">Status</span>
                      <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm bg-white">
                        <option value="OPEN">Open</option>
                        <option value="CLOSED_TEMPORARILY">Temporarily closed</option>
                      </select>
                    </label>
                    <button
                      type="button"
                      disabled={!detailsDirty || !!saving || !title.trim()}
                      onClick={saveDetails}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#1a73e8] text-white text-[13px] font-semibold hover:bg-[#1765cc] disabled:opacity-40"
                    >
                      {saving === 'Business details' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                      Save details
                    </button>
                  </div>
                </div>
                <div className="rounded-xl bg-gray-50 border border-gray-200 px-3 py-2.5 text-[12px] text-gray-600 space-y-1">
                  {loc.categories?.primaryCategory?.displayName ? (
                    <p>
                      <span className="text-gray-400">Category:</span> {loc.categories.primaryCategory.displayName}
                      {loc.categories.additionalCategories?.length
                        ? ` · ${loc.categories.additionalCategories.map((c) => c.displayName).filter(Boolean).join(', ')}`
                        : ''}
                    </p>
                  ) : null}
                  {address ? (
                    <p className="inline-flex items-start gap-1"><MapPin className="h-3 w-3 mt-0.5 shrink-0 text-gray-400" />{address}</p>
                  ) : null}
                  <p className="text-[11px] text-gray-400">Categories and address are edited in the Google Business Profile app so Google can re-verify them.</p>
                </div>
              </section>

              {/* Hours */}
              <section className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Opening hours</p>
                  <button
                    type="button"
                    disabled={!hoursDirty || !!saving}
                    onClick={saveHours}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-900 text-white text-[12px] font-semibold disabled:opacity-40"
                  >
                    {saving === 'Opening hours' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                    Save hours
                  </button>
                </div>
                <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
                  {DAYS.map((day) => {
                    const d = hours[day];
                    return (
                      <div key={day} className="flex items-start gap-3 px-3 py-2">
                        <span className="w-9 text-[13px] font-semibold text-gray-800 pt-1.5">{DAY_LABEL[day]}</span>
                        <label className="inline-flex items-center gap-1.5 pt-1.5 text-[12px] text-gray-600 shrink-0">
                          <input
                            type="checkbox"
                            checked={!d.closed}
                            onChange={(e) =>
                              setHours((h) => ({
                                ...h,
                                [day]: e.target.checked
                                  ? { closed: false, periods: d.periods.length ? d.periods : [{ open: '09:00', close: '17:00' }] }
                                  : { ...d, closed: true },
                              }))
                            }
                          />
                          {d.closed ? 'Closed' : 'Open'}
                        </label>
                        {!d.closed ? (
                          <div className="flex-1 space-y-1.5">
                            {d.periods.map((p, i) => (
                              <div key={i} className="flex items-center gap-1.5">
                                <input
                                  type="time"
                                  value={p.open}
                                  onChange={(e) => setHours((h) => ({ ...h, [day]: { ...d, periods: d.periods.map((pp, j) => (j === i ? { ...pp, open: e.target.value } : pp)) } }))}
                                  className="rounded-md border border-gray-300 px-1.5 py-1 text-[12px]"
                                />
                                <span className="text-gray-400 text-[12px]">–</span>
                                <input
                                  type="time"
                                  value={p.close}
                                  onChange={(e) => setHours((h) => ({ ...h, [day]: { ...d, periods: d.periods.map((pp, j) => (j === i ? { ...pp, close: e.target.value } : pp)) } }))}
                                  className="rounded-md border border-gray-300 px-1.5 py-1 text-[12px]"
                                />
                                {d.periods.length > 1 ? (
                                  <button type="button" aria-label="Remove hours" onClick={() => setHours((h) => ({ ...h, [day]: { ...d, periods: d.periods.filter((_, j) => j !== i) } }))} className="h-6 w-6 rounded-md text-gray-400 hover:bg-gray-100 flex items-center justify-center">
                                    <X className="h-3 w-3" />
                                  </button>
                                ) : null}
                              </div>
                            ))}
                            {d.periods.length < 3 ? (
                              <button type="button" onClick={() => setHours((h) => ({ ...h, [day]: { ...d, periods: [...d.periods, { open: '', close: '' }] } }))} className="text-[11px] text-[#1a73e8] font-semibold inline-flex items-center gap-1">
                                <Plus className="h-3 w-3" /> Add hours
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Special hours */}
              <section className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Holidays & special hours</p>
                  <button
                    type="button"
                    disabled={!specialDirty || !!saving}
                    onClick={saveSpecial}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-900 text-white text-[12px] font-semibold disabled:opacity-40"
                  >
                    {saving === 'Special hours' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                    Save
                  </button>
                </div>
                <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
                  {special.length === 0 ? (
                    <p className="text-[12px] text-gray-500 px-3 py-3">No special hours set. Add a date to mark a holiday closure or different hours.</p>
                  ) : (
                    special.map((s, i) => (
                      <div key={`${s.startDate.year}-${s.startDate.month}-${s.startDate.day}-${i}`} className="flex items-center gap-2 px-3 py-2 flex-wrap">
                        <span className="text-[13px] font-medium text-gray-800 w-28 shrink-0">{fmtGoogleDate(s.startDate)}</span>
                        <label className="inline-flex items-center gap-1.5 text-[12px] text-gray-600">
                          <input
                            type="checkbox"
                            checked={!!s.closed}
                            onChange={(e) => setSpecial((list) => list.map((x, j) => (j === i ? (e.target.checked ? { startDate: x.startDate, closed: true } : { startDate: x.startDate, openTime: { hours: 9 }, closeTime: { hours: 17 } }) : x)))}
                          />
                          Closed
                        </label>
                        {!s.closed ? (
                          <span className="inline-flex items-center gap-1.5">
                            <input type="time" value={toInputTime(s.openTime)} onChange={(e) => setSpecial((list) => list.map((x, j) => (j === i ? { ...x, openTime: fromInputTime(e.target.value) } : x)))} className="rounded-md border border-gray-300 px-1.5 py-1 text-[12px]" />
                            <span className="text-gray-400 text-[12px]">–</span>
                            <input type="time" value={toInputTime(s.closeTime)} onChange={(e) => setSpecial((list) => list.map((x, j) => (j === i ? { ...x, closeTime: fromInputTime(e.target.value) } : x)))} className="rounded-md border border-gray-300 px-1.5 py-1 text-[12px]" />
                          </span>
                        ) : (
                          <span className="text-[12px] text-gray-400">all day</span>
                        )}
                        <button type="button" aria-label="Remove" onClick={() => setSpecial((list) => list.filter((_, j) => j !== i))} className="ml-auto h-7 w-7 rounded-md text-gray-400 hover:bg-gray-100 hover:text-red-600 flex items-center justify-center">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))
                  )}
                  <div className="flex items-center gap-2 px-3 py-2">
                    <input type="date" value={newSpecialDate} onChange={(e) => setNewSpecialDate(e.target.value)} className="rounded-md border border-gray-300 px-2 py-1 text-[12px]" />
                    <button
                      type="button"
                      disabled={!newSpecialDate}
                      onClick={() => {
                        const [y, m, d] = newSpecialDate.split('-').map(Number);
                        if (!y || !m || !d) return;
                        setSpecial((list) => [...list, { startDate: { year: y, month: m, day: d }, closed: true }]);
                        setNewSpecialDate('');
                      }}
                      className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#1a73e8] disabled:opacity-40"
                    >
                      <Plus className="h-3.5 w-3.5" /> Add date
                    </button>
                  </div>
                </div>
              </section>

              {/* People */}
              <section className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Owners & managers</p>
                  <button type="button" onClick={() => setShowInvite((v) => !v)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-[12px] font-semibold text-gray-800 hover:bg-gray-50">
                    <UserPlus className="h-3.5 w-3.5" /> Invite
                  </button>
                </div>
                {showInvite ? (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 flex flex-col sm:flex-row gap-2">
                    <input
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      inputMode="email"
                      placeholder="person@gmail.com"
                      className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                    <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as 'MANAGER' | 'OWNER')} className="rounded-lg border border-gray-300 px-2 py-2 text-sm bg-white">
                      <option value="MANAGER">Manager</option>
                      <option value="OWNER">Owner</option>
                    </select>
                    <button
                      type="button"
                      disabled={!!saving || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail.trim())}
                      onClick={() => {
                        void adminAction(`Invited ${inviteEmail.trim()}`, { op: 'invite', email: inviteEmail.trim(), role: inviteRole }).then(() => {
                          setInviteEmail('');
                          setShowInvite(false);
                        });
                      }}
                      className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#1a73e8] text-white text-[13px] font-semibold hover:bg-[#1765cc] disabled:opacity-40"
                    >
                      {saving?.startsWith('Invited') ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send invite'}
                    </button>
                  </div>
                ) : null}
                <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
                  {data?.adminsError && data.admins.length === 0 ? (
                    <p className="text-[12px] text-amber-700 px-3 py-3">Couldn't load people ({data.adminsError}). Only owners can see this list.</p>
                  ) : data?.admins.length === 0 ? (
                    <p className="text-[12px] text-gray-500 px-3 py-3">No one else has access to this profile.</p>
                  ) : (
                    data?.admins.map((a) => {
                      const isPrimary = a.role === 'PRIMARY_OWNER';
                      return (
                        <div key={a.name} className="flex items-center gap-3 px-3 py-2.5">
                          <span className="h-8 w-8 rounded-full bg-gray-100 text-gray-600 text-[12px] font-bold flex items-center justify-center shrink-0 uppercase">
                            {(a.email || '?').charAt(0)}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-[13px] font-medium text-gray-900 truncate">{a.email || 'Google account'}</p>
                            <p className="text-[11px] text-gray-500">
                              {ROLE_LABEL[a.role] || a.role}
                              {a.pendingInvitation ? <span className="ml-1.5 text-amber-600 font-semibold">· Invite pending</span> : null}
                            </p>
                          </div>
                          {!isPrimary ? (
                            <>
                              <select
                                value={a.role === 'OWNER' ? 'OWNER' : 'MANAGER'}
                                disabled={!!saving || a.pendingInvitation}
                                onChange={(e) => void adminAction('Role updated', { op: 'updateRole', name: a.name, role: e.target.value })}
                                className="rounded-md border border-gray-300 px-1.5 py-1 text-[12px] bg-white disabled:opacity-50"
                              >
                                <option value="MANAGER">Manager</option>
                                <option value="OWNER">Owner</option>
                              </select>
                              <button
                                type="button"
                                aria-label="Remove access"
                                disabled={!!saving}
                                onClick={() => {
                                  if (window.confirm(`Remove ${a.email || 'this person'} from the Business Profile?`)) {
                                    void adminAction('Access removed', { op: 'remove', name: a.name });
                                  }
                                }}
                                className="h-7 w-7 rounded-md text-gray-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center disabled:opacity-50"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          ) : null}
                        </div>
                      );
                    })
                  )}
                </div>
                <p className="text-[10px] text-gray-400">
                  Invitees need a Google account. Transferring primary ownership is done in the Google Business Profile app.
                </p>
              </section>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
